import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { useTheme } from '../ThemeContext';

const NAV_ITEMS = [
  { label: 'Dashboard',     path: '/',              icon: '▣', roles: ['super-admin', 'hospital-admin', 'reception', 'doctor'] },
  { label: 'Hospitals',     path: '/hospitals',     icon: '🏥', roles: ['super-admin'] },
  { label: 'Patients',      path: '/patients',      icon: '👥', roles: ['super-admin', 'hospital-admin', 'reception', 'doctor'] },
  { label: 'Appointments',  path: '/appointments',  icon: '📅', roles: ['super-admin', 'hospital-admin', 'reception', 'doctor'] },
  { label: 'Staff',         path: '/staff',         icon: '👔', roles: ['super-admin', 'hospital-admin'] },
  { label: 'Notifications', path: '/notifications', icon: '💬', roles: ['super-admin', 'hospital-admin', 'reception'] },
  { label: 'Audit Logs',    path: '/audit',         icon: '🔍', roles: ['super-admin', 'hospital-admin'] },
];

const ROLE_COLORS = {
  'super-admin':    { bg: 'rgba(245,158,11,0.15)',  text: '#f59e0b' },
  'hospital-admin': { bg: 'rgba(14,165,233,0.15)',  text: '#0ea5e9' },
  'doctor':         { bg: 'rgba(34,197,94,0.15)',   text: '#22c55e' },
  'reception':      { bg: 'rgba(100,116,139,0.15)', text: '#64748b' },
  'nurse':          { bg: 'rgba(236,72,153,0.15)',  text: '#ec4899' },
};

export default function Sidebar({ user, mobileOpen, onClose }) {
  const navigate             = useNavigate();
  const location             = useLocation();
  const { theme, toggleTheme } = useTheme();
  const role                 = user?.role || 'reception';
  const roleColor            = ROLE_COLORS[role] || ROLE_COLORS['reception'];
  const isLight              = theme === 'light';

  const visibleNav = NAV_ITEMS.filter(n => n.roles.includes(role));

  const handleSignOut = async () => {
    try { await signOut({ global: true }); } catch {}
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  const handleNav = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <>
      {mobileOpen && (
        <div onClick={onClose} className="mobile-overlay"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 199, display: 'none' }} />
      )}

      <div className={`sidebar ${mobileOpen ? 'sidebar-open' : ''}`} style={{
        width: 220, minHeight: '100vh',
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--sidebar-border)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', left: 0, top: 0, zIndex: 200,
        transition: 'transform 0.25s ease, background 0.25s ease',
        boxShadow: isLight ? '2px 0 12px rgba(0,0,0,0.06)' : 'none',
      }}>

        {/* Logo */}
        <div style={{ padding: '22px 18px 16px', borderBottom: '1px solid var(--sidebar-sep)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, fontWeight: 800, color: '#fff',
              boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
            }}>P</div>
            <div>
              <div style={{ color: 'var(--sidebar-text)', fontWeight: 800, fontSize: 15, letterSpacing: 0.3 }}>Praveda</div>
              <div style={{ color: 'var(--sidebar-text-sub)', fontSize: 9, marginTop: 1, letterSpacing: 0.5 }}>BY BTRIX TECHNOLOGIES</div>
            </div>
            <span onClick={onClose} className="sidebar-close"
              style={{ marginLeft: 'auto', color: 'var(--sidebar-text-sub)', fontSize: 18, cursor: 'pointer', display: 'none' }}>✕</span>
          </div>
        </div>

        {/* Hospital badge */}
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--sidebar-sep)' }}>
          <div style={{
            background: 'var(--sidebar-badge-bg)',
            border: '1px solid var(--sidebar-badge-border)',
            borderRadius: 10, padding: '9px 12px',
          }}>
            <div style={{ fontSize: 9, color: 'var(--sidebar-badge-label)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {role === 'super-admin' ? 'Platform' : 'Hospital'}
            </div>
            <div style={{ color: 'var(--sidebar-badge-value)', fontSize: 12, fontWeight: 700 }}>
              {role === 'super-admin' ? 'Btrix Technologies' : user?.hospital_id || '—'}
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ padding: '10px 10px', flex: 1, overflowY: 'auto' }}>
          {visibleNav.map(n => {
            const active = location.pathname === n.path;
            return (
              <div key={n.path} onClick={() => handleNav(n.path)} style={{
                display: 'flex', alignItems: 'center', gap: 11,
                padding: '9px 12px', borderRadius: 10, marginBottom: 2,
                cursor: 'pointer',
                background: active ? 'var(--sidebar-nav-active-bg)' : 'transparent',
                border: active ? '1px solid var(--sidebar-nav-active-bdr)' : '1px solid transparent',
                transition: 'all 0.15s',
              }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--sidebar-nav-hover)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 15, opacity: active ? 1 : 0.55 }}>{n.icon}</span>
                <span style={{
                  fontSize: 13, fontWeight: active ? 700 : 400,
                  color: active ? 'var(--sidebar-nav-active-text)' : 'var(--sidebar-nav-inactive)',
                  letterSpacing: 0.2,
                }}>{n.label}</span>
                {active && (
                  <div style={{
                    marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%',
                    background: '#6366f1', boxShadow: '0 0 8px rgba(99,102,241,0.7)',
                  }} />
                )}
              </div>
            );
          })}
        </nav>

        {/* Theme toggle */}
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--sidebar-sep)' }}>
          <button onClick={toggleTheme} style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: isLight ? '#f1f5f9' : 'rgba(99,102,241,0.1)',
            border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(99,102,241,0.2)'}`,
            borderRadius: 10, padding: '9px 14px', cursor: 'pointer',
            transition: 'background 0.2s',
          }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: isLight ? '#4f46e5' : '#a5b4fc' }}>
              {isLight ? '🌙  Dark Mode' : '☀️  Light Mode'}
            </span>
            {/* pill track */}
            <div style={{
              width: 36, height: 20, borderRadius: 10,
              background: isLight ? 'rgba(234,179,8,0.3)' : 'rgba(99,102,241,0.4)',
              position: 'relative', transition: 'background 0.3s',
            }}>
              <div style={{
                position: 'absolute', top: 3,
                left: isLight ? 17 : 3,
                width: 14, height: 14, borderRadius: '50%',
                background: isLight ? '#fbbf24' : '#a5b4fc',
                transition: 'left 0.3s, background 0.3s',
                boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }} />
            </div>
          </button>
        </div>

        {/* User */}
        <div style={{ padding: '12px 14px', borderTop: '1px solid var(--sidebar-sep)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 700,
              boxShadow: '0 2px 8px rgba(99,102,241,0.35)',
            }}>{user?.name?.[0]?.toUpperCase() || 'U'}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--sidebar-text)', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.name || 'User'}
              </div>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                background: roleColor.bg, color: roleColor.text, textTransform: 'capitalize',
              }}>{role.replace('-', ' ')}</span>
            </div>
            <button onClick={handleSignOut} title="Sign out" style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 7, padding: '5px 8px', cursor: 'pointer',
              color: '#f87171', fontSize: 11, fontWeight: 600, flexShrink: 0,
            }}>Out</button>
          </div>
        </div>
      </div>
    </>
  );
}
